import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser, useAuth } from "@clerk/clerk-react";
import { toast } from "sonner";
import type { Database } from "@/lib/supabase/supabase-types";
import { courseService, lessonService } from "@/lib/api/services";

// Base types from database
type Course = Database["public"]["Tables"]["courses"]["Row"];
type CourseInsert = Database["public"]["Tables"]["courses"]["Insert"];
type CourseUpdate = Database["public"]["Tables"]["courses"]["Update"];
type CourseEnrollment = Database["public"]["Tables"]["course_enrollments"]["Row"];
type Lesson = Database["public"]["Tables"]["lessons"]["Row"];
type LessonInsert = Database["public"]["Tables"]["lessons"]["Insert"];
type LessonUpdate = Database["public"]["Tables"]["lessons"]["Update"];
type CourseSection = Database["public"]["Tables"]["course_sections"]["Row"];
type LessonProgress = Database["public"]["Tables"]["lesson_progress"]["Row"];

// Unified types that work for both admins and freelancers
export type UnifiedCourse = Course & {
  enrollment?: CourseEnrollment | null;  // For enrolled users
  canEdit?: boolean;                     // For admins
  lessonsCount?: number;                 // Summary info
};

// Extended lesson type with progress
export type LessonWithProgress = Lesson & {
  progress?: LessonProgress | null;
};

export type UnifiedCourseDetail = UnifiedCourse & {
  sections: (CourseSection & {
    lessons: LessonWithProgress[];
  })[];
  lessons: LessonWithProgress[]; // Flat list for easy access
};

/**
 * Unified courses hook that works for both admins and freelancers
 * - Admins see all courses (published + unpublished) with edit capabilities
 * - Freelancers see only published courses with enrollment status
 * - Uses single cache key for consistency
 */
export function useUnifiedCourses() {
  const { user } = useUser();
  const { sessionClaims } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is admin - check both JWT and fresh publicMetadata to handle stale JWT on first login
  const isAdmin = sessionClaims?.app_role === 'admin' || user?.publicMetadata?.app_role === 'admin';

  const coursesQuery = useQuery({
    queryKey: ["courses"],
    queryFn: async () => {
      // Use API service for fetching courses
      const courses = isAdmin 
        ? await courseService.getCourses(false) // Admins get all courses
        : await courseService.getPublishedCourses(); // Freelancers get only published

      // Get user's enrollments if they're authenticated
      let enrollments: CourseEnrollment[] = [];
      if (user?.id) {
        try {
          enrollments = await courseService.getMyEnrollments();
        } catch (error) {
          // If endpoint fails, continue without enrollments
          console.warn('Failed to fetch user enrollments:', error);
        }
      }

      // Get lesson counts for all courses using backend API
      const courseIds = courses.map(c => c.id);
      const lessonCountPromises = courseIds.map(courseId => 
        courseService.getCourseLessonCount(courseId).catch(() => 0)
      );
      const lessonCountsArray = await Promise.all(lessonCountPromises);
      const lessonCounts: Record<string, number> = {};
      courseIds.forEach((courseId, index) => {
        lessonCounts[courseId] = lessonCountsArray[index];
      });

      // Combine courses with enrollment status and admin capabilities
      const unifiedCourses: UnifiedCourse[] = courses.map(course => ({
        ...course,
        enrollment: enrollments.find(e => e.course_id === course.id) || null,
        canEdit: isAdmin,
        lessonsCount: lessonCounts[course.id] || 0,
      }));

      return unifiedCourses;
    },
    enabled: true,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Course enrollment mutation (for freelancers)
  const enrollMutation = useMutation({
    mutationFn: async (courseId: string) => {
      if (!user?.id) throw new Error("No user ID");

      // Use backend API to enroll
      return await courseService.enrollInCourse(courseId);
    },
    onMutate: async (courseId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      // Snapshot the previous values
      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);

      const optimisticEnrollment: CourseEnrollment = {
        id: `temp-${Date.now()}`,
        user_id: user?.id || '',
        course_id: courseId,
        status: "active",
        enrolled_at: new Date().toISOString(),
        progress_percent: 0,
        completed_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Optimistically update courses list
      if (previousCourses) {
        const updatedCourses = previousCourses.map(course =>
          course.id === courseId
            ? { ...course, enrollment: optimisticEnrollment }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      return { previousCourses };
    },
    onError: (err, _courseId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      console.error('Course enrollment failed:', err);
      toast.error('Failed to enroll in course. Please try again.');
    },
    onSuccess: (data, courseId) => {
      // Update the courses list cache with the actual server data
      const currentCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (currentCourses) {
        const updatedCourses = currentCourses.map(course =>
          course.id === courseId
            ? { ...course, enrollment: data }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      toast.success('Successfully enrolled in course!');
    },
  });

  // Course creation mutation (for admins)
  const createCourseMutation = useMutation({
    mutationFn: async (courseData: CourseInsert) => {
      if (!isAdmin) throw new Error("Not authorized to create courses");
      return await courseService.createCourse(courseData);
    },
    onMutate: async (newCourse) => {
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);

      // Create optimistic course
      const optimisticCourse: UnifiedCourse = {
        id: `temp-${Date.now()}`,
        slug: newCourse.slug || '',
        title: newCourse.title || '',
        description: newCourse.description || null,
        thumbnail_url: newCourse.thumbnail_url || null,
        is_published: newCourse.is_published || false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        canEdit: isAdmin,
        lessonsCount: 0,
      };

      // Optimistically update
      if (previousCourses) {
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], [optimisticCourse, ...previousCourses]);
      }

      return { previousCourses };
    },
    onError: (err, _newCourse, context) => {
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      console.error('Course creation failed:', err);
      toast.error('Failed to create course. Please try again.');
    },
    onSuccess: (data) => {
      // Replace optimistic data with real data
      const currentCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (currentCourses) {
        const updatedCourses = currentCourses.map(course =>
          course.id.toString().startsWith('temp-')
            ? { ...data, canEdit: isAdmin, lessonsCount: 0 }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      toast.success('Course created successfully!');
    },
  });

  // Course update mutation (for admins)
  const updateCourseMutation = useMutation({
    mutationFn: async ({ id, ...courseData }: { id: string } & CourseUpdate) => {
      if (!isAdmin) throw new Error("Not authorized to update courses");
      return await courseService.updateCourse(id, courseData);
    },
    onMutate: async ({ id, ...updateData }) => {
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);

      // Optimistically update
      if (previousCourses) {
        const updatedCourses = previousCourses.map(course =>
          course.id === id
            ? { ...course, ...updateData, updated_at: new Date().toISOString() }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      return { previousCourses };
    },
    onError: (err, _variables, context) => {
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      console.error('Course update failed:', err);
      toast.error('Failed to update course. Please try again.');
    },
    onSuccess: (data) => {
      // Update with real server data
      const currentCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (currentCourses) {
        const updatedCourses = currentCourses.map(course =>
          course.id === data.id
            ? { ...course, ...data, updated_at: data.updated_at }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      toast.success('Course updated successfully!');
    },
  });

  // Course deletion mutation (for admins)
  const deleteCourseMutation = useMutation({
    mutationFn: async (courseId: string) => {
      if (!isAdmin) throw new Error("Not authorized to delete courses");
      await courseService.deleteCourse(courseId);
      return courseId;
    },
    onMutate: async (courseId) => {
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);

      // Optimistically remove course
      if (previousCourses) {
        const updatedCourses = previousCourses.filter(course => course.id !== courseId);
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      return { previousCourses };
    },
    onError: (err, _courseId, context) => {
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      console.error('Course deletion failed:', err);
      toast.error('Failed to delete course. Please try again.');
    },
    onSuccess: () => {
      toast.success('Course deleted successfully!');
    },
  });

  return {
    // Data
    courses: coursesQuery.data || [],
    isLoading: coursesQuery.isLoading,
    error: coursesQuery.error,

    // User permissions
    isAdmin,

    // Enrollment actions (available to all authenticated users)
    enroll: user?.id ? enrollMutation.mutate : undefined,
    isEnrolling: enrollMutation.isPending,

    // Admin actions
    createCourse: isAdmin ? createCourseMutation.mutate : undefined,
    isCreating: createCourseMutation.isPending,
    updateCourse: isAdmin ? updateCourseMutation.mutate : undefined,
    isUpdating: updateCourseMutation.isPending,
    deleteCourse: isAdmin ? deleteCourseMutation.mutate : undefined,
    isDeleting: deleteCourseMutation.isPending,
  };
}

/**
 * Unified individual course hook that works for both admins and freelancers
 * - Fetches course details including lessons and sections
 * - Shows enrollment status for freelancers
 * - Shows edit capabilities for admins
 * - Uses unified cache keys
 */
export function useUnifiedCourse(slug: string) {
  const { user } = useUser();
  const { sessionClaims } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is admin - check both JWT and fresh publicMetadata to handle stale JWT on first login
  const isAdmin = sessionClaims?.app_role === 'admin' || user?.publicMetadata?.app_role === 'admin';

  // Note: Course cache optimization could be added here if needed

  const courseQuery = useQuery({
    queryKey: ["courses", "detail", slug],
    queryFn: async () => {
      // Use API service for fetching course by slug
      const course = await courseService.getCourseBySlug(slug);

      // Check if course is published for non-admin users
      if (!isAdmin && !course.is_published) {
        throw new Error("Course not found");
      }

      let enrollment: CourseEnrollment | null = null;

      // Get user's enrollment for this course if authenticated
      if (user?.id) {
        try {
          const enrollments = await courseService.getMyEnrollments();
          enrollment = enrollments.find(e => e.course_id === course.id) || null;
        } catch (error) {
          // If enrollment fetching fails, continue without enrollment
          console.warn('Failed to fetch enrollment:', error);
        }
      }

      // Get course sections and lessons using backend API
      const [sections, lessons] = await Promise.all([
        courseService.getCourseSections(course.id),
        courseService.getCourseLessons(course.id),
      ]);

      // Get lesson progress if user is enrolled - use API service
      let lessonProgressMap: Record<string, LessonProgress> = {};
      if (enrollment && user?.id) {
        try {
          const { lessonProgressService } = await import("@/lib/api/services");
          const progressData = await lessonProgressService.getProgressByCourse(course.id);

          // Create a map for quick lookup
          if (progressData) {
            lessonProgressMap = progressData.reduce((acc, progress) => {
              acc[progress.lesson_id] = progress;
              return acc;
            }, {} as Record<string, LessonProgress>);
          }
        } catch (error) {
          // If API fails, log error but don't throw - progress is optional
          console.warn("Failed to fetch lesson progress from API:", error);
        }
      }

      // Add progress to lessons
      const lessonsWithProgress: LessonWithProgress[] = lessons.map(lesson => ({
        ...lesson,
        progress: lessonProgressMap[lesson.id] || null,
      }));

      // Group lessons by section
      const sectionsWithLessons = sections.map(section => ({
        ...section,
        lessons: lessonsWithProgress.filter(lesson => lesson.section_id === section.id),
      }));

      const courseDetail: UnifiedCourseDetail = {
        ...course,
        enrollment: enrollment || null,
        canEdit: isAdmin,
        lessonsCount: lessons.length,
        sections: sectionsWithLessons,
        lessons: lessonsWithProgress,
      };

      return courseDetail;
    },
    enabled: !!slug,
    staleTime: 20 * 60 * 1000, // 20 minutes
  });

  // Course enrollment mutation (reuse from main hook but with course detail cache updates)
  const enrollMutation = useMutation({
    mutationFn: async (courseId: string) => {
      if (!user?.id) throw new Error("No user ID");

      // Use backend API to enroll
      return await courseService.enrollInCourse(courseId);
    },
    onMutate: async (courseId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      // Snapshot the previous values
      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      const previousCourseDetail = queryClient.getQueryData<UnifiedCourseDetail>(["courses", "detail", slug]);

      const optimisticEnrollment: CourseEnrollment = {
        id: `temp-${Date.now()}`,
        user_id: user?.id || '',
        course_id: courseId,
        status: "active",
        enrolled_at: new Date().toISOString(),
        progress_percent: 0,
        completed_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Optimistically update courses list
      if (previousCourses) {
        const updatedCourses = previousCourses.map(course =>
          course.id === courseId
            ? { ...course, enrollment: optimisticEnrollment }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      // Optimistically update course detail
      if (previousCourseDetail && previousCourseDetail.id === courseId) {
        const updatedCourseDetail: UnifiedCourseDetail = {
          ...previousCourseDetail,
          enrollment: optimisticEnrollment,
        };
        queryClient.setQueryData<UnifiedCourseDetail>(["courses", "detail", slug], updatedCourseDetail);
      }

      return { previousCourses, previousCourseDetail };
    },
    onError: (err, _courseId, context) => {
      // Rollback both caches
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      if (context?.previousCourseDetail) {
        queryClient.setQueryData(["courses", "detail", slug], context.previousCourseDetail);
      }
      console.error('Course enrollment failed:', err);
      toast.error('Failed to enroll in course. Please try again.');
    },
    onSuccess: (data, courseId) => {
      // Update both caches with actual server data
      const currentCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (currentCourses) {
        const updatedCourses = currentCourses.map(course =>
          course.id === courseId
            ? { ...course, enrollment: data }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      const currentCourseDetail = queryClient.getQueryData<UnifiedCourseDetail>(["courses", "detail", slug]);
      if (currentCourseDetail && currentCourseDetail.id === courseId) {
        const updatedCourseDetail: UnifiedCourseDetail = {
          ...currentCourseDetail,
          enrollment: data,
        };
        queryClient.setQueryData<UnifiedCourseDetail>(["courses", "detail", slug], updatedCourseDetail);
      }

      toast.success('Successfully enrolled in course!');
    },
  });

  return {
    // Data
    course: courseQuery.data,
    isLoading: courseQuery.isLoading,
    error: courseQuery.error,

    // User permissions
    isAdmin,

    // Actions
    enroll: user?.id && courseQuery.data ? enrollMutation.mutate : undefined,
    isEnrolling: enrollMutation.isPending,
  };
}

/**
 * Unified lesson management hook for course details
 * - Provides lesson CRUD operations for admins
 * - Updates unified cache keys consistently
 * - Works with the unified course detail hook
 */
export function useUnifiedCourseLessons(courseId: string) {
  const { user } = useUser();
  const { sessionClaims } = useAuth();
  const queryClient = useQueryClient();

  // Check if user is admin - check both JWT and fresh publicMetadata to handle stale JWT on first login
  const isAdmin = sessionClaims?.app_role === 'admin' || user?.publicMetadata?.app_role === 'admin';

  // Create lesson mutation (for admins)
  const createLessonMutation = useMutation({
    mutationFn: async (lessonData: LessonInsert) => {
      if (!isAdmin) throw new Error("Admin access required");
      return await lessonService.createLesson(lessonData);
    },
    onMutate: async (newLesson) => {
      // Cancel related queries
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      // Get current course data from cache
      const allCourseDetailQueries = queryClient.getQueriesData({ queryKey: ["courses", "detail"] });
      const relevantCourseDetail = allCourseDetailQueries.find(([, data]: [any, any]) =>
        data && data.id === courseId
      );

      const previousCourseDetail = relevantCourseDetail ? relevantCourseDetail[1] as UnifiedCourseDetail : null;

      // Create optimistic lesson
      const optimisticLesson: Lesson = {
        id: `temp-${Date.now()}`,
        course_id: newLesson.course_id || courseId,
        section_id: newLesson.section_id || null,
        lesson_type: newLesson.lesson_type || 'video',
        title: newLesson.title || '',
        description: newLesson.description || null,
        vimeo_video_id: newLesson.vimeo_video_id || null,
        duration_seconds: newLesson.duration_seconds || null,
        position: newLesson.position || 1,
        is_required: newLesson.is_required !== false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Optimistically update course detail cache
      if (previousCourseDetail && relevantCourseDetail) {
        const updatedLessons = [...previousCourseDetail.lessons, optimisticLesson]
          .sort((a: any, b: any) => a.position - b.position);

        const updatedSections = previousCourseDetail.sections.map(section => ({
          ...section,
          lessons: section.id === optimisticLesson.section_id
            ? [...section.lessons, optimisticLesson].sort((a: any, b: any) => a.position - b.position)
            : section.lessons
        }));

        const updatedCourseDetail: UnifiedCourseDetail = {
          ...previousCourseDetail,
          lessons: updatedLessons,
          sections: updatedSections,
          lessonsCount: updatedLessons.length,
        };

        queryClient.setQueryData(relevantCourseDetail[0], updatedCourseDetail);
      }

      // Optimistically update main courses list lesson count
      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (previousCourses) {
        const updatedCourses = previousCourses.map(course =>
          course.id === courseId
            ? { ...course, lessonsCount: (course.lessonsCount || 0) + 1 }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      return { previousCourseDetail, courseDetailKey: relevantCourseDetail?.[0], previousCourses };
    },
    onError: (err, _newLesson, context) => {
      // Rollback optimistic updates
      if (context?.previousCourseDetail && context.courseDetailKey) {
        queryClient.setQueryData(context.courseDetailKey, context.previousCourseDetail);
      }
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      console.error('Lesson creation failed:', err);
      toast.error('Failed to create lesson. Please try again.');
    },
    onSuccess: (data) => {
      // Update course detail with real lesson data
      const allCourseDetailQueries = queryClient.getQueriesData({ queryKey: ["courses", "detail"] });
      allCourseDetailQueries.forEach(([queryKey, courseDetail]: [any, any]) => {
        if (courseDetail && courseDetail.id === courseId) {
          const updatedLessons = courseDetail.lessons
            .filter((lesson: Lesson) => !lesson.id.toString().startsWith('temp-'))
            .concat(data)
            .sort((a: any, b: any) => a.position - b.position);

          const updatedSections = courseDetail.sections.map((section: any) => ({
            ...section,
            lessons: section.id === data.section_id
              ? section.lessons.filter((lesson: Lesson) => !lesson.id.toString().startsWith('temp-')).concat(data).sort((a: any, b: any) => a.position - b.position)
              : section.lessons.filter((lesson: Lesson) => !lesson.id.toString().startsWith('temp-'))
          }));

          const updatedCourseDetail: UnifiedCourseDetail = {
            ...courseDetail,
            lessons: updatedLessons,
            sections: updatedSections,
            lessonsCount: updatedLessons.length,
          };

          queryClient.setQueryData(queryKey, updatedCourseDetail);
        }
      });

      // Update main courses list with real lesson count
      const currentCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (currentCourses) {
        const updatedCourses = currentCourses.map(course =>
          course.id === courseId
            ? { ...course, lessonsCount: (course.lessonsCount || 0) }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      toast.success('Lesson created successfully!');
    },
  });

  // Update lesson mutation (for admins)
  const updateLessonMutation = useMutation({
    mutationFn: async ({ id, ...lessonData }: { id: string } & LessonUpdate) => {
      if (!isAdmin) throw new Error("Admin access required");
      return await lessonService.updateLesson(id, lessonData);
    },
    onMutate: async ({ id, ...updateData }) => {
      // Cancel related queries
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      // Get current course data from cache and update optimistically
      const allCourseDetailQueries = queryClient.getQueriesData({ queryKey: ["courses", "detail"] });
      const relevantCourseDetail = allCourseDetailQueries.find(([, data]: [any, any]) =>
        data && data.lessons?.some((lesson: Lesson) => lesson.id === id)
      );

      const previousCourseDetail = relevantCourseDetail ? relevantCourseDetail[1] as UnifiedCourseDetail : null;

      if (previousCourseDetail && relevantCourseDetail) {
        const updatedLessons = previousCourseDetail.lessons.map(lesson =>
          lesson.id === id
            ? { ...lesson, ...updateData, updated_at: new Date().toISOString() }
            : lesson
        ).sort((a, b) => a.position - b.position);

        const updatedSections = previousCourseDetail.sections.map(section => ({
          ...section,
          lessons: section.lessons.map(lesson =>
            lesson.id === id
              ? { ...lesson, ...updateData, updated_at: new Date().toISOString() }
              : lesson
          ).sort((a, b) => a.position - b.position)
        }));

        const updatedCourseDetail: UnifiedCourseDetail = {
          ...previousCourseDetail,
          lessons: updatedLessons,
          sections: updatedSections,
        };

        queryClient.setQueryData(relevantCourseDetail[0], updatedCourseDetail);
      }

      return { previousCourseDetail, courseDetailKey: relevantCourseDetail?.[0] };
    },
    onError: (err, _variables, context) => {
      // Rollback optimistic updates
      if (context?.previousCourseDetail && context.courseDetailKey) {
        queryClient.setQueryData(context.courseDetailKey, context.previousCourseDetail);
      }
      console.error('Lesson update failed:', err);
      toast.error('Failed to update lesson. Please try again.');
    },
    onSuccess: (data) => {
      // Update with real server data
      const allCourseDetailQueries = queryClient.getQueriesData({ queryKey: ["courses", "detail"] });
      allCourseDetailQueries.forEach(([queryKey, courseDetail]: [any, any]) => {
        if (courseDetail && courseDetail.lessons?.some((lesson: Lesson) => lesson.id === data.id)) {
          const updatedLessons = courseDetail.lessons.map((lesson: Lesson) =>
            lesson.id === data.id ? data : lesson
          ).sort((a: any, b: any) => a.position - b.position);

          const updatedSections = courseDetail.sections.map((section: any) => ({
            ...section,
            lessons: section.lessons.map((lesson: Lesson) =>
              lesson.id === data.id ? data : lesson
            ).sort((a: any, b: any) => a.position - b.position)
          }));

          const updatedCourseDetail: UnifiedCourseDetail = {
            ...courseDetail,
            lessons: updatedLessons,
            sections: updatedSections,
          };

          queryClient.setQueryData(queryKey, updatedCourseDetail);
        }
      });

      toast.success('Lesson updated successfully!');
    },
  });

  // Delete lesson mutation (for admins)
  const deleteLessonMutation = useMutation({
    mutationFn: async (lessonId: string) => {
      if (!isAdmin) throw new Error("Admin access required");
      await lessonService.deleteLesson(lessonId);
      return lessonId;
    },
    onMutate: async (lessonId) => {
      // Cancel related queries
      await queryClient.cancelQueries({ queryKey: ["courses"] });

      // Get current course data from cache
      const allCourseDetailQueries = queryClient.getQueriesData({ queryKey: ["courses", "detail"] });
      const relevantCourseDetail = allCourseDetailQueries.find(([, data]: [any, any]) =>
        data && data.lessons?.some((lesson: Lesson) => lesson.id === lessonId)
      );

      const previousCourseDetail = relevantCourseDetail ? relevantCourseDetail[1] as UnifiedCourseDetail : null;

      // Optimistically remove lesson
      if (previousCourseDetail && relevantCourseDetail) {
        const updatedLessons = previousCourseDetail.lessons.filter(lesson => lesson.id !== lessonId);
        const updatedSections = previousCourseDetail.sections.map(section => ({
          ...section,
          lessons: section.lessons.filter(lesson => lesson.id !== lessonId)
        }));

        const updatedCourseDetail: UnifiedCourseDetail = {
          ...previousCourseDetail,
          lessons: updatedLessons,
          sections: updatedSections,
          lessonsCount: updatedLessons.length,
        };

        queryClient.setQueryData(relevantCourseDetail[0], updatedCourseDetail);
      }

      // Update main courses list lesson count
      const previousCourses = queryClient.getQueryData<UnifiedCourse[]>(["courses"]);
      if (previousCourses && previousCourseDetail) {
        const updatedCourses = previousCourses.map(course =>
          course.id === previousCourseDetail.id
            ? { ...course, lessonsCount: Math.max((course.lessonsCount || 0) - 1, 0) }
            : course
        );
        queryClient.setQueryData<UnifiedCourse[]>(["courses"], updatedCourses);
      }

      return { previousCourseDetail, courseDetailKey: relevantCourseDetail?.[0], previousCourses };
    },
    onError: (err, _lessonId, context) => {
      // Rollback optimistic updates
      if (context?.previousCourseDetail && context.courseDetailKey) {
        queryClient.setQueryData(context.courseDetailKey, context.previousCourseDetail);
      }
      if (context?.previousCourses) {
        queryClient.setQueryData(["courses"], context.previousCourses);
      }
      console.error('Lesson deletion failed:', err);
      toast.error('Failed to delete lesson. Please try again.');
    },
    onSuccess: () => {
      toast.success('Lesson deleted successfully!');
    },
  });

  return {
    // Admin actions
    createLesson: isAdmin ? createLessonMutation.mutate : undefined,
    isCreating: createLessonMutation.isPending,
    updateLesson: isAdmin ? updateLessonMutation.mutate : undefined,
    isUpdating: updateLessonMutation.isPending,
    deleteLesson: isAdmin ? deleteLessonMutation.mutate : undefined,
    isDeleting: deleteLessonMutation.isPending,

    // User permissions
    isAdmin,
  };
}
